import * as React from 'react'

/*
 * Holds the selected tenant language per tenant for the session. The selection
 * is tenant-scoped (the languages available depend on the tenant) and
 * session-scoped: it survives a reload within the tab via sessionStorage but is
 * cleared when the session ends. Persisting across sessions/devices is out of
 * scope for VIS-252.
 *
 * The destination/behaviour after selecting a language is intentionally not
 * wired to anything else yet (unspecified in VIS-252) — this context is the
 * single place a future consumer can read the chosen language from.
 */

type LanguageContextValue = {
  /** The selected language code for the given tenant, or null if none chosen. */
  getLanguage: (tenantId: string) => string | null
  setLanguage: (tenantId: string, code: string) => void
}

const LanguageContext = React.createContext<LanguageContextValue | null>(null)
const STORAGE_KEY = 'visitone.tenantLanguages'

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [byTenant, setByTenant] = React.useState<Record<string, string>>({})

  React.useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY)
      if (raw) setByTenant(JSON.parse(raw) as Record<string, string>)
    } catch {
      // Ignore unavailable or malformed session storage.
    }
  }, [])

  const setLanguage = React.useCallback((tenantId: string, code: string) => {
    setByTenant((prev) => {
      const next = { ...prev, [tenantId]: code }
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next))
      } catch {
        // Non-fatal: selection still applies for the current in-memory session.
      }
      return next
    })
  }, [])

  const value = React.useMemo<LanguageContextValue>(
    () => ({
      getLanguage: (tenantId: string) => byTenant[tenantId] ?? null,
      setLanguage,
    }),
    [byTenant, setLanguage],
  )

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
}

export function useLanguageContext() {
  const context = React.useContext(LanguageContext)
  if (!context) throw new Error('useLanguageContext must be used within a LanguageProvider')
  return context
}
